import { useCallback, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { Form as FormikForm, Formik, Field, FieldProps } from 'formik'
import { PhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js/max'

import { FormProps, Form, LayoutItem } from '../../../ana-toolbox'
import { FormLayout } from '../../components/form-layout/form-layout'
import { SubmitButton } from '../../components/submit-button/submit-button'
import { validationSchema } from '../../utils/fields/contactDetails'
import { getFormInitialErrors } from '../../utils/helpers/getFormInitialErrors'
import { AddressLayoutItem } from '../../utils/shared/AddressLayoutItem'
import { combinedFields } from '../../utils/yup/main'

const contactDetailsLayout = (): LayoutItem[] => [
  { type: 'custom', key: 'street', xs: 12, render: AddressLayoutItem },
  { type: 'field', field: combinedFields.zipCode, xs: 12 },
  { type: 'field', field: combinedFields.city, xs: 12 },
  { type: 'field', field: combinedFields.email, xs: 12 },
  { type: 'field', field: combinedFields.phone, xs: 12 },
]

const removeLeadingZero = (mobilePhone: string) => {
  try {
    const { countryCallingCode, nationalNumber } = parsePhoneNumberFromString(mobilePhone) as PhoneNumber

    if (countryCallingCode && nationalNumber) {
      return `+${countryCallingCode}${nationalNumber.replace(/^0/, '')}`
    }
  } catch {
    return mobilePhone
  }

  return mobilePhone
}

export const ContactDetailsForm = ({ onSubmit, initialValues, portalAttr, loading, innerRef }: FormProps) => {
  const layout = useMemo(() => contactDetailsLayout(), [])
  const { t } = useTranslation()

  const handleSubmit = useCallback(
    (values: Partial<Form>) => {
      onSubmit({ ...values, phone: removeLeadingZero(values.phone as string) })
    },
    [onSubmit]
  )

  const submitButton = (
    <Field>
      {(props: FieldProps) => {
        return (
          <SubmitButton
            onClick={() => {
              props.form.submitForm()
            }}
            loading={loading}
            disabled={!props.form.isValid}
          >
            {t('next')}
          </SubmitButton>
        )
      }}
    </Field>
  )

  return (
    <Formik
      initialValues={initialValues}
      initialErrors={getFormInitialErrors(validationSchema, initialValues)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      innerRef={innerRef}
    >
      <FormikForm>
        <FormLayout portalAttr={portalAttr} items={layout} submitButton={submitButton} />
      </FormikForm>
    </Formik>
  )
}
